import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * A callback with a permanent identity that always calls the LATEST function it
 * was given. Hands a per-render closure to a memoised child without defeating
 * its `memo` (a fresh closure per render would re-render every row). Only for
 * handlers invoked from events, never during render: the ref is refreshed in a
 * layout effect, i.e. after commit and before any event can fire.
 */
export function useStableCallback<Args extends unknown[], R>(
  fn: ((...args: Args) => R) | undefined,
): (...args: Args) => R | undefined {
  const ref = useRef(fn);
  useLayoutEffect(() => {
    ref.current = fn;
  });
  return useCallback((...args: Args) => ref.current?.(...args), []);
}
